<template>
  <div class="container d-flex justify-content-center align-items-center vh-100">
    <div class="card p-4 shadow-lg form-wrapper">
      <img src="./../../assets/logo_sofia.png" alt="Logo" class="img-fluid mx-auto d-block mb-3" />
      <form @submit.prevent="handleResetPassword" class="form">
        <div class="mb-3">
          <label for="password" class="form-label">New Password</label>
          <div class="input-group">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              id="password"
              class="form-control"
              placeholder="Enter new password"
              required
              @blur="touched.password = true"
            />
          <div class="input-group-append">
                <span class="input-group-text text-muted bg-transparent border-left-0 rounded-right" style="height:100%;">
                  <i @click="toggleShow" class="fas eye-shape"
                    :class="{ 'fa-eye-slash': showPassword, 'fa-eye': !showPassword }"></i>
                </span>
              </div>
            </div>
            <small v-if="touched.password && !password" class="text-danger">Password is required.</small>
            <small v-else-if="touched.password && password.length < 6" class="text-danger">Password must be at least 6 characters.</small>
            <div class="strength-bar">
              <span v-for="i in 5" :key="i" :class="{ active: i <= validCount }"></span>
            </div>
            <!-- Rules -->
            <div class="rules">
              <div v-for="(rule, i) in passwordRules" :key="i" class="rule" :class="{ valid: rule.test(password) }">
                <span class="icon">
                  {{ rule.test(password) ? '✓' : '✕' }}
                </span>
                {{ rule.label }}
              </div>
            </div>
        </div>
        <div class="mb-3">
          <label for="confirmPassword" class="form-label">Confirm Password</label>
          <div class="input-group">
          <input
            v-model="confirmPassword"
            :type="showPassword ? 'text' : 'password'"
            id="confirmPassword"
            class="form-control"
            placeholder="Confirm new password"
            required
            @blur="touched.confirmPassword = true"
          />
          <div class="input-group-append">
                <span class="input-group-text text-muted bg-transparent border-left-0 rounded-right" style="height:100%;">
                  <i @click="toggleShowCofirm" class="fas eye-shape"
                    :class="{ 'fa-eye-slash': showConfirmPassword, 'fa-eye': !showConfirmPassword }"></i>
                </span>
              </div>
            </div>
            <small v-if="touched.confirmPassword && !confirmPassword" class="text-danger">Confirm Password is required.</small>
            <small v-else-if="touched.confirmPassword && confirmPassword !== password" class="text-danger">Passwords do not match.</small>
        </div>
        <button
          type="submit"
          class="btn btn-primary w-100"
          :disabled="!isFormValid"
        >
          Reset Password
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref ,computed} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { resetPasswordService } from '@/services/api/authService';

const password = ref('');
const confirmPassword = ref('');
const router = useRouter();
const store = useStore();
const toast = useToast();
const touched = ref({
  password: false,
  confirmPassword: false,
});
let showPassword = ref(false);
let showConfirmPassword = ref(false);
const passwordRules = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'At least one uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'At least one lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'At least one number', test: p => /[0-9]/.test(p) },
  { label: 'At least one special character', test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
]
const validCount = computed(() =>
  passwordRules.filter(r => r.test(password.value)).length
)
const toggleShow = () => {
  showPassword.value = !showPassword.value;
};
const toggleShowCofirm = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

const isFormValid = computed(() => {
  return password.value.length >= 6 && password.value === confirmPassword.value ;
});

const handleResetPassword = async () => {
  const token = store.getters.token;

  const resetPasswordServices = async (password, token) => {
      try {
        const result =await resetPasswordService(password, token );
          if (result.status === 200) {
            return true;
          } else {
            return false;
          }
        // eslint-disable-next-line 
      } catch (error) {
        toast.error('An unexpected error occurred');
        return false;
      }
    };

  const success = await resetPasswordServices(password.value, token);
  if (success) {
    console.error = 'Password reset successfully!';
    toast.success('Password reset successfully!');
    router.push('/login');
  } else {
    console.error = 'Failed to reset password. Please try again.';
    toast.error('Failed to reset password. Please try again.');
  }
};
</script>

<style scoped>

.form-wrapper {
  max-width: 400px;
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.form-control {
  padding: 0.8em;
  border-radius: 4px;
  border: 1px solid #ddd;
  box-sizing: border-box;
}

.btn:disabled {
  opacity: 0.65;
}
.password-wrapper {
  margin-top: 10px;
}
/* INPUT */
.password-input {
  position: relative;
}

.password-input input {
  width: 100%;
  padding: 14px 44px 14px 16px;
  font-size: 16px;
  border-radius: 12px;
  border: 2px solid #d0d7e2;
  outline: none;
  transition: 0.2s ease;
}

.password-input input:focus {
  border-color: #5b8cff;
  box-shadow: 0 0 0 4px rgba(91, 140, 255, 0.2);
}

/* EYE ICON */
.eye-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  opacity: 0.6;
}

/* STRENGTH BAR */
.strength-bar {
  display: flex;
  gap: 6px;
  margin: 10px 0;
}

.strength-bar span {
  flex: 1;
  height: 6px;
  border-radius: 10px;
  background: #e5e7eb;
}

.strength-bar span.active:nth-child(1),
.strength-bar span.active:nth-child(2) {
  background: #e5533d;
}

.strength-bar span.active:nth-child(3),
.strength-bar span.active:nth-child(4) {
  background: #f4b400;
}

.strength-bar span.active:nth-child(5) {
  background: #2ecc71;
}

/* RULES */
.rules {
  display: grid;
  grid-template-columns: 1fr 1fr;

  font-size: 12px;
}

.rule {
  display: flex;
  align-items: center;
  color: #6b7280;
}

.rule .icon {
  width: 18px;
  margin-right: 8px;
}

.rule.valid {
  color: #2ecc71;
}
</style>
